import { useEffect } from 'react';
import type { CSSProperties } from 'react';

import { css, keyframes } from '@emotion/css';

import { color, movimiento, suave } from '#components/mobile/ui/tokens';

/**
 * «Suficiente este mes» al estilo Rumbo: sobre el botón ✓ de la ficha se
 * cierra un anillo de rosa de los vientos (trazo de acento que arranca en el
 * norte), se dibuja el ✓, el conjunto se asienta con el muelle de la marca y
 * sale una onda de agua con cuatro destellos en los puntos cardinales y unas
 * gotas en diagonal. Una sola vez (≈ 1,1 s), superpuesta y sin captar toques.
 * Al quitarla, el anillo y el ✓ se desvanecen (≈ 0,3 s). Con «reducir
 * movimiento» solo un fundido breve del ✓. Dibujo propio (SVG + keyframes).
 */

export const DURACION_SUFICIENTE = 1100;
export const DURACION_QUITAR = 300;
const DURACION_REDUCIDA = 200;

/** Tamaño de la capa (px) y radio del anillo, centrados en el botón de 44. */
const LADO = 88;
const C = LADO / 2;
const RADIO_ANILLO = 14.5;

/** Mismo ✓ que `Icono nombre="check"` (24 u → 18 px), centrado en la capa. */
const CHECK = 'M20 6 9 17l-5-5';
const ESCALA_CHECK = 18 / 24;

const asentar = keyframes({
  '0%': { transform: 'scale(0.82)' },
  '18%': { transform: 'scale(0.9)' },
  '62%': { transform: 'scale(1.08)' },
  '100%': { transform: 'scale(1)' },
});

const cerrarAnillo = keyframes({
  '0%': { strokeDashoffset: 1, opacity: 0.4 },
  '8%': { opacity: 1 },
  '46%, 100%': { strokeDashoffset: 0, opacity: 1 },
});

const dibujarCheck = keyframes({
  '0%, 30%': { strokeDashoffset: 1 },
  '60%, 100%': { strokeDashoffset: 0 },
});

const onda = keyframes({
  '0%, 40%': { transform: 'scale(0.9)', opacity: 0 },
  '46%': { transform: 'scale(1)', opacity: 0.55 },
  '100%': { transform: 'scale(2.6)', opacity: 0 },
});

const destello = keyframes({
  '0%, 42%': { transform: 'translateY(0) scale(0.3)', opacity: 0 },
  '55%': { transform: 'translateY(-4px) scale(1)', opacity: 1 },
  '100%': { transform: 'translateY(-11px) scale(0.6)', opacity: 0 },
});

const desvanecer = keyframes({
  '0%': { transform: 'scale(1)', opacity: 1 },
  '100%': { transform: 'scale(0.8)', opacity: 0 },
});

const aparecer = keyframes({
  '0%': { opacity: 0 },
  '100%': { opacity: 1 },
});

const sinMovimiento = css({
  '@media (prefers-reduced-motion: reduce)': {
    '& *': { animation: 'none !important' },
  },
});

export function reduceMovimiento(): boolean {
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

const capa = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  width: LADO,
  height: LADO,
  marginTop: -C,
  marginLeft: -C,
  pointerEvents: 'none',
  zIndex: 5,
  lineHeight: 0,
} as const;

/** Anillo y ✓ (el estado «Suficiente» ya asentado). */
function AnilloCheck({
  anillo,
  check,
}: {
  anillo?: CSSProperties;
  check?: CSSProperties;
}) {
  return (
    <>
      <circle
        cx={C}
        cy={C}
        r={RADIO_ANILLO}
        fill={suave(color.accent, 12)}
        stroke={color.accent}
        strokeWidth={1.8}
        strokeLinecap="round"
        pathLength={1}
        strokeDasharray="1 1"
        transform={`rotate(-90 ${C} ${C})`}
        style={anillo}
      />
      <g
        transform={`translate(${C - 9} ${C - 9}) scale(${ESCALA_CHECK})`}
        fill="none"
        stroke={color.accent}
        strokeWidth={2.6}
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d={CHECK} pathLength={1} strokeDasharray="1 1" style={check} />
      </g>
    </>
  );
}

/** Un destello en forma de punta de rosa de los vientos, girado `angulo`°. */
function Destello({ angulo, retardo }: { angulo: number; retardo: number }) {
  return (
    <g transform={`rotate(${angulo} ${C} ${C})`}>
      <path
        d={`M${C} ${C - 23}l2.2 4.6-2.2 4.6-2.2-4.6z`}
        fill={color.accent}
        style={{
          transformBox: 'fill-box',
          transformOrigin: '50% 100%',
          animation: `${destello} ${DURACION_SUFICIENTE}ms ${movimiento.suave} ${retardo}ms both`,
        }}
      />
    </g>
  );
}

/** Gota pequeña en diagonal que se aleja y se apaga. */
function Gota({ angulo }: { angulo: number }) {
  return (
    <g transform={`rotate(${angulo} ${C} ${C})`}>
      <circle
        cx={C}
        cy={C - 21}
        r={1.7}
        fill={suave(color.accent, 70)}
        style={{
          transformBox: 'fill-box',
          transformOrigin: '50% 50%',
          animation: `${destello} ${DURACION_SUFICIENTE}ms ${movimiento.suave} 60ms both`,
        }}
      />
    </g>
  );
}

/**
 * Animación de transición superpuesta al botón ✓: `poner` (anillo que se
 * cierra, ✓ que se dibuja, onda y destellos) o `quitar` (fundido de salida).
 */
export function AnimacionSuficiente({
  modo,
  onFin,
}: {
  modo: 'poner' | 'quitar';
  onFin: () => void;
}) {
  const reducir = reduceMovimiento();
  const poner = modo === 'poner';
  const duracion = reducir
    ? poner
      ? DURACION_REDUCIDA
      : 0
    : poner
      ? DURACION_SUFICIENTE
      : DURACION_QUITAR;

  useEffect(() => {
    const id = window.setTimeout(onFin, duracion);
    return () => window.clearTimeout(id);
  }, [onFin, duracion]);

  if (reducir) {
    if (!poner) {
      return null;
    }
    return (
      <span
        aria-hidden
        data-testid="suficiente-reducida"
        style={{
          ...capa,
          animation: `${aparecer} ${DURACION_REDUCIDA}ms ease-out both`,
        }}
      >
        <svg viewBox={`0 0 ${LADO} ${LADO}`} width={LADO} height={LADO}>
          <AnilloCheck
            anillo={{ strokeDashoffset: 0 }}
            check={{ strokeDashoffset: 0 }}
          />
        </svg>
      </span>
    );
  }

  if (!poner) {
    return (
      <span
        aria-hidden
        data-testid="suficiente-quitar"
        className={sinMovimiento}
        style={capa}
      >
        <svg
          viewBox={`0 0 ${LADO} ${LADO}`}
          width={LADO}
          height={LADO}
          style={{
            animation: `${desvanecer} ${DURACION_QUITAR}ms ${movimiento.suave} both`,
          }}
        >
          <AnilloCheck
            anillo={{ strokeDashoffset: 0 }}
            check={{ strokeDashoffset: 0 }}
          />
        </svg>
      </span>
    );
  }

  const ms = `${DURACION_SUFICIENTE}ms`;
  return (
    <span
      aria-hidden
      data-testid="suficiente-poner"
      className={sinMovimiento}
      style={capa}
    >
      <svg
        viewBox={`0 0 ${LADO} ${LADO}`}
        width={LADO}
        height={LADO}
        style={{ position: 'absolute', inset: 0, overflow: 'visible' }}
      >
        <circle
          data-testid="suficiente-onda"
          cx={C}
          cy={C}
          r={RADIO_ANILLO}
          fill="none"
          stroke={color.accent}
          strokeWidth={1.4}
          style={{
            transformOrigin: `${C}px ${C}px`,
            animation: `${onda} ${ms} ${movimiento.suave} both`,
          }}
        />
        {[0, 90, 180, 270].map((a, i) => (
          <Destello key={a} angulo={a} retardo={i * 25} />
        ))}
        {[45, 135, 225, 315].map(a => (
          <Gota key={a} angulo={a} />
        ))}
      </svg>
      <svg
        viewBox={`0 0 ${LADO} ${LADO}`}
        width={LADO}
        height={LADO}
        style={{
          position: 'absolute',
          inset: 0,
          transformOrigin: '50% 50%',
          animation: `${asentar} ${ms} ${movimiento.muelle} both`,
        }}
      >
        <AnilloCheck
          anillo={{ animation: `${cerrarAnillo} ${ms} ease-out both` }}
          check={{
            animation: `${dibujarCheck} ${ms} cubic-bezier(.6,0,.3,1) both`,
          }}
        />
      </svg>
    </span>
  );
}

/** Marca quieta mientras la categoría está en «Suficiente»: anillo fino. */
export function MarcaSuficiente({
  'data-testid': testId,
}: {
  'data-testid'?: string;
}) {
  return (
    <span aria-hidden data-testid={testId} style={{ ...capa, zIndex: 0 }}>
      <svg viewBox={`0 0 ${LADO} ${LADO}`} width={LADO} height={LADO}>
        <circle
          cx={C}
          cy={C}
          r={RADIO_ANILLO}
          fill={suave(color.accent, 12)}
          stroke={color.accent}
          strokeWidth={1.8}
        />
      </svg>
    </span>
  );
}
